import throttle from 'lodash/throttle'
import { useEffect, useRef, useState } from 'react'

/** Ignore sub-pixel and rubber-band jitter, which would otherwise flap the bar. */
const MOVEMENT_THRESHOLD = 4

/**
 * Reports whether a fixed header should be hidden, based on scroll direction:
 * hidden while scrolling down, shown again on any upward scroll.
 *
 * Always shown within `revealAbove` pixels of the top, so the header can never
 * be stuck hidden at the top of the page.
 */
export function useHideOnScroll(revealAbove = 80) {
  const [isHidden, setIsHidden] = useState(false)
  const lastScrollY = useRef(0)

  useEffect(() => {
    lastScrollY.current = window.scrollY

    const handleScroll = throttle(() => {
      const currentY = window.scrollY

      if (currentY <= revealAbove) {
        setIsHidden(false)
      } else if (currentY > lastScrollY.current + MOVEMENT_THRESHOLD) {
        setIsHidden(true)
      } else if (currentY < lastScrollY.current - MOVEMENT_THRESHOLD) {
        setIsHidden(false)
      }

      lastScrollY.current = currentY
    }, 100)

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      handleScroll.cancel()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [revealAbove])

  return isHidden
}
